import { Plugin, ButtonView } from 'ckeditor5';

/**
 * Custom link plugin that provides a balloon UI with PillEditor integration
 * for both link URL and link text, supporting merge tags.
 */
export default class CustomLinkPlugin extends Plugin {
    static get pluginName() {
        return 'CustomLinkPlugin';
    }

    static get requires() {
        return ['LinkEditing', 'ContextualBalloon'];
    }

    init() {
        const editor = this.editor;
        const config = editor.config.get('customLinkUI') || {};
        
        if (config.disabled) {
            return;
        }

        this._config = config;
        this._balloon = null;
        this._formView = null;
        this._isInserting = false;
        this._currentLinkElement = null;
        this._currentLinkRange = null;
        this._hasDomFallbackValues = false;
        this._domRootWithListener = null;
        this._balloonStackId = 'customLink';

        // Store current values
        this._currentLinkText = '';
        this._currentLinkUrl = '';

        // Bind methods
        this._boundMouseDownHandler = this._handleDocumentMouseDown.bind(this);
        this._boundMouseUpHandler = this._handleDocumentMouseUp.bind(this);
        this._boundDomClickHandler = this._handleDomClick.bind(this);
        this._boundAttachDomRootClickListener = this._attachDomRootClickListener.bind(this);
        document.addEventListener('mousedown', this._boundMouseDownHandler, true);
        document.addEventListener('mouseup', this._boundMouseUpHandler, true);

        this._attachDomRootClickListener();
        this.listenTo(editor.editing.view.document, 'render', this._boundAttachDomRootClickListener);

        this._createToolbarButton();
        this._attachBalloonHideListeners();
    }

    _attachDomRootClickListener() {
        const domRoot = this.editor?.editing?.view?.getDomRoot?.();
        if (!domRoot || this._domRootWithListener === domRoot) {
            return;
        }

        if (this._domRootWithListener) {
            this._domRootWithListener.removeEventListener('click', this._boundDomClickHandler, true);
        }

        domRoot.addEventListener('click', this._boundDomClickHandler, true);
        this._domRootWithListener = domRoot;
    }

    /**
     * Create the toolbar button for inserting links
     */
    _createToolbarButton() {
        const editor = this.editor;

        editor.ui.componentFactory.add('customLink', locale => {
            const button = new ButtonView(locale);

            button.set({
                label: 'Link',
                icon: this._getLinkIcon(),
                tooltip: true
            });

            button.on('execute', () => {
                this._showBalloon(true);
            });

            return button;
        });
    }

    /**
     * Attach listeners to hide balloon when clicking outside or selection changes
     */
    _attachBalloonHideListeners() {
        const editor = this.editor;
        
        // Listen for clicks outside the balloon (on document level)
        this.listenTo(document, 'mousedown', (evt, domEvent) => {
            if (!this._balloon || !this._balloon.visibleView || this._balloon.visibleView !== this._formView) {
                return;
            }

            const target = domEvent.target;
            if (!(target instanceof Element)) {
                return;
            }

            // Don't hide if clicking inside the balloon
            const balloonElement = this._formView?.element;
            if (balloonElement && (balloonElement === target || balloonElement.contains(target))) {
                return;
            }

            // Don't hide if clicking inside merge tag modals
            if (target.closest('.modal-root, .MuiBackdrop-root, .MuiPopover-root, .MuiDialog-root')) {
                return;
            }

            // Don't hide if clicking on a link in the editor (we want to open edit mode)
            const editorRoot = editor.editing.view.getDomRoot();
            if (editorRoot && editorRoot.contains(target)) {
                const clickedLink = target.closest('a');
                if (clickedLink) {
                    return;
                }
            }

            // Hide the balloon
            this._hideBalloon();
        });

        // Listen for selection changes to hide balloon when moving away from link
        this.listenTo(editor.model.document.selection, 'change', () => {
            if (!this._balloon || !this._balloon.visibleView || this._balloon.visibleView !== this._formView) {
                return;
            }

            // If we're in insert mode, don't auto-hide
            if (this._isInserting) {
                return;
            }

            // Check if selection is still on a link
            const selection = editor.model.document.selection;
            const linkHref = selection.getAttribute('linkHref');
            
            // If no longer on a link, hide the balloon
            if (!linkHref) {
                this._hideBalloon();
            }
        });
    }

    _handleDomClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const clickedLink = target.closest('a');
        if (!clickedLink) {
            return;
        }

        event.preventDefault();

        this._currentLinkText = clickedLink.textContent || '';
        this._currentLinkUrl = clickedLink.getAttribute('href') || '';
        this._hasDomFallbackValues = true;

        // Open immediately from DOM fallback values.
        this._currentLinkElement = null;
        this._currentLinkRange = null;
        this._showBalloon(false);

        // Let CKEditor finish selection update first.
        setTimeout(() => {
            this._openEditorForSelectedLink();
        }, 0);
    }

    _openEditorForSelectedLink() {
        const editor = this.editor;
        const selection = editor.model.document.selection;
        const linkHref = selection.getAttribute('linkHref');

        if (!linkHref) {
            return false;
        }

        const position = selection.getFirstPosition();
        const candidate = position?.textNode || position?.nodeBefore || position?.nodeAfter;

        if (!candidate || !candidate.is('$text') || candidate.getAttribute('linkHref') !== linkHref) {
            return false;
        }

        this._currentLinkElement = candidate;
        this._currentLinkRange = this._findLinkRange(candidate);
        this._hasDomFallbackValues = false;
        this._showBalloon(false);
        return true;
    }

    /**
     * Show the balloon form for inserting or editing a link
     */
    _showBalloon(isInserting) {
        const editor = this.editor;
        this._isInserting = isInserting;

        // Get contextual balloon plugin
        if (!this._balloon) {
            this._balloon = editor.plugins.get('ContextualBalloon');
        }

        // Create the form if it doesn't exist
        if (!this._formView) {
            this._createFormView();
        }

        // Read current values when editing
        if (!isInserting) {
            if (this._currentLinkElement) {
                this._readCurrentLinkValues();
            } else if (!this._hasDomFallbackValues) {
                this._currentLinkText = '';
                this._currentLinkUrl = '';
            }
        } else {
            // When inserting, use selected text as link text
            const selection = editor.model.document.selection;
            const selectedText = this._getSelectedText();
            this._currentLinkText = selectedText;
            this._currentLinkUrl = '';
            this._hasDomFallbackValues = false;
        }

        // Unmount existing PillEditors before remounting with new values
        this._unmountPillEditors();

        // Mount PillEditors
        this._mountPillEditors();

        // Show balloon (or update position if already shown)
        if (!this._balloon.hasView(this._formView)) {
            this._balloon.add({
                view: this._formView,
                position: this._getBalloonPositionData(),
                stackId: this._balloonStackId
            });
        } else {
            this._balloon.updatePosition(this._getBalloonPositionData());
        }

        this._balloon.showStack(this._balloonStackId);
    }

    /**
     * Create the form view with mount points for PillEditors
     */
    _createFormView() {
        // Create container element
        const formElement = document.createElement('div');
        formElement.className = 'ck ck-link-form ck-reset_all-excluded';
        formElement.style.padding = '16px';
        formElement.style.minWidth = '350px';

        // Create link text container
        const linkTextContainer = document.createElement('div');
        linkTextContainer.className = 'ck ck-link-field-container ck-reset_all-excluded';
        linkTextContainer.style.marginBottom = '12px';
        formElement.appendChild(linkTextContainer);

        // Create link URL container
        const linkUrlContainer = document.createElement('div');
        linkUrlContainer.className = 'ck ck-link-field-container ck-reset_all-excluded';
        linkUrlContainer.style.marginBottom = '12px';
        formElement.appendChild(linkUrlContainer);

        // Create button container
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'ck ck-button-container';
        buttonContainer.style.display = 'flex';
        buttonContainer.style.gap = '8px';
        buttonContainer.style.justifyContent = 'flex-end';

        // Create Insert/Update button
        const submitButton = document.createElement('button');
        submitButton.type = 'button';
        submitButton.className = 'ck ck-button ck-button-save';
        submitButton.textContent = 'Insert';
        submitButton.style.flexGrow = '1';
        this._styleButton(submitButton);
        
        submitButton.addEventListener('click', () => {
            this._handleSubmit();
        });

        // Create Cancel button
        const cancelButton = document.createElement('button');
        cancelButton.type = 'button';
        cancelButton.className = 'ck ck-button ck-button-cancel';
        cancelButton.textContent = 'Cancel';
        cancelButton.style.padding = '6px 16px';
        cancelButton.style.borderRadius = '4px';
        cancelButton.style.border = '1px solid #ccc';
        cancelButton.style.background = 'white';
        cancelButton.style.cursor = 'pointer';
        
        cancelButton.addEventListener('click', () => {
            this._hideBalloon();
        });

        buttonContainer.appendChild(submitButton);
        buttonContainer.appendChild(cancelButton);
        formElement.appendChild(buttonContainer);

        // Store references
        this._linkTextContainer = linkTextContainer;
        this._linkUrlContainer = linkUrlContainer;
        this._submitButton = submitButton;

        // Create a simple view object that CKEditor balloon can use
        this._formView = {
            element: formElement,
            render: () => {},
            destroy: () => {}
        };
    }

    /**
     * Unmount PillEditor components via callbacks
     */
    _unmountPillEditors() {
        const config = this._config;
        
        if (config.onLinkTextPillEditorUnmount) {
            config.onLinkTextPillEditorUnmount();
        }
        if (config.onLinkUrlPillEditorUnmount) {
            config.onLinkUrlPillEditorUnmount();
        }
    }

    /**
     * Mount PillEditor components via callbacks
     */
    _mountPillEditors() {
        const config = this._config;

        // Mount link text PillEditor
        if (config.onLinkTextPillEditorMount && this._linkTextContainer) {
            config.onLinkTextPillEditorMount({
                container: this._linkTextContainer,
                initialValue: this._currentLinkText,
                onChange: (value) => {
                    this._currentLinkText = value;
                }
            });
        }

        // Mount link URL PillEditor
        if (config.onLinkUrlPillEditorMount && this._linkUrlContainer) {
            config.onLinkUrlPillEditorMount({
                container: this._linkUrlContainer,
                initialValue: this._currentLinkUrl,
                onChange: (value) => {
                    this._currentLinkUrl = value;
                }
            });
        }

        // Update button text based on mode
        if (this._submitButton) {
            this._submitButton.textContent = this._isInserting ? 'Insert' : 'Update';
        }
    }

    /**
     * Handle form submission (Insert or Update link)
     */
    _handleSubmit() {
        const editor = this.editor;

        if (!this._currentLinkUrl) {
            return; // Don't allow empty URL
        }

        editor.model.change(writer => {
            if (this._isInserting) {
                // Insert new link
                const selection = editor.model.document.selection;
                const linkCommand = editor.commands.get('link');
                
                // If there's selected text, use it
                if (!selection.isCollapsed) {
                    linkCommand.execute(this._currentLinkUrl);
                } else {
                    // Insert link text and URL
                    const linkText = this._currentLinkText || this._currentLinkUrl;
                    const insertPosition = selection.getFirstPosition();
                    const textNode = writer.createText(linkText, { linkHref: this._currentLinkUrl });
                    writer.insert(textNode, insertPosition);
                    writer.setSelection(textNode, 'on');
                }
            } else {
                // Update existing link
                if (this._currentLinkElement) {
                    // Find the entire linked range (all consecutive text nodes with same linkHref)
                    const linkedRange = this._currentLinkRange || this._findLinkRange(this._currentLinkElement);
                    
                    if (linkedRange) {
                        // Remove old link text
                        writer.remove(linkedRange);
                        
                        // Insert new link text with new URL
                        const newText = this._currentLinkText || this._currentLinkUrl;
                        const textNode = writer.createText(newText, { linkHref: this._currentLinkUrl });
                        writer.insert(textNode, linkedRange.start);
                    }
                }
            }
        });

        this._hideBalloon();
    }

    /**
     * Hide the balloon form
     */
    _hideBalloon() {
        if (this._balloon && this._formView) {
            this._balloon.remove(this._formView);
        }

        // Unmount PillEditors
        this._unmountPillEditors();

        this._currentLinkElement = null;
        this._currentLinkRange = null;
        this._hasDomFallbackValues = false;
    }

    /**
     * Read current values when editing an existing link
     */
    _readCurrentLinkValues() {
        // Don't overwrite DOM fallback values
        if (this._hasDomFallbackValues) {
            return;
        }

        if (!this._currentLinkElement) {
            this._currentLinkText = '';
            this._currentLinkUrl = '';
            return;
        }

        // Get link URL
        this._currentLinkUrl = this._currentLinkElement.getAttribute('linkHref') || '';
        
        // Get link text from the entire linked range
        const linkedRange = this._currentLinkRange || this._findLinkRange(this._currentLinkElement);
        this._currentLinkRange = linkedRange;
        const rangeText = linkedRange ? this._getTextFromRange(linkedRange) : '';
        const elementText = this._getLinkElementText(this._currentLinkElement);
        this._currentLinkText = rangeText || elementText;
    }

    /**
     * Get text content from a link element
     */
    _getLinkElementText(linkElement) {
        if (!linkElement || !linkElement.is('$text')) {
            return '';
        }

        return linkElement.data;
    }

    /**
     * Find the entire range of a link (all consecutive text with same linkHref)
     */
    _findLinkRange(textNode) {
        if (!textNode || !textNode.is('$text')) {
            return null;
        }

        const linkHref = textNode.getAttribute('linkHref');
        if (!linkHref) {
            return null;
        }

        const editor = this.editor;
        const model = editor.model;
        
        // Find start of link
        let start = model.createPositionBefore(textNode);
        let currentNode = textNode.previousSibling;
        
        while (currentNode && currentNode.is('$text') && currentNode.getAttribute('linkHref') === linkHref) {
            start = model.createPositionBefore(currentNode);
            currentNode = currentNode.previousSibling;
        }
        
        // Find end of link
        let end = model.createPositionAfter(textNode);
        currentNode = textNode.nextSibling;
        
        while (currentNode && currentNode.is('$text') && currentNode.getAttribute('linkHref') === linkHref) {
            end = model.createPositionAfter(currentNode);
            currentNode = currentNode.nextSibling;
        }
        
        return model.createRange(start, end);
    }

    /**
     * Get text content from a range
     */
    _getTextFromRange(range) {
        let text = '';
        for (const item of range.getItems()) {
            if (item.is('$text')) {
                text += item.data;
            }
        }
        return text;
    }

    /**
     * Get selected text from editor
     */
    _getSelectedText() {
        const editor = this.editor;
        const selection = editor.model.document.selection;
        
        if (selection.isCollapsed) {
            return '';
        }

        let text = '';
        for (const range of selection.getRanges()) {
            for (const item of range.getItems()) {
                if (item.is('$text')) {
                    text += item.data;
                }
            }
        }
        return text;
    }

    /**
     * Get balloon position data
     */
    _getBalloonPositionData() {
        const editor = this.editor;
        const view = editor.editing.view;
        const viewDocument = view.document;

        let target;

        if (this._isInserting) {
            target = () => view.domConverter.viewRangeToDom(
                viewDocument.selection.getFirstRange()
            );
        } else {
            const modelRange = this._currentLinkRange;

            if (modelRange) {
                const mapper = editor.editing.mapper;
                const viewRange = mapper.toViewRange(modelRange);
                target = () => view.domConverter.viewRangeToDom(viewRange);
            } else {
                target = () => view.domConverter.viewRangeToDom(
                    viewDocument.selection.getFirstRange()
                );
            }
        }

        return {
            target
        };
    }

    /**
     * Style the submit button with app branding
     */
    _styleButton(button) {
        button.style.display = 'inline-flex';
        button.style.alignItems = 'center';
        button.style.justifyContent = 'center';
        button.style.backgroundColor = '#00f399';
        button.style.color = '#191936';
        button.style.border = 'none';
        button.style.borderRadius = '4px';
        button.style.fontSize = '14px';
        button.style.fontWeight = '500';
        button.style.padding = '6px 16px';
        button.style.cursor = 'pointer';
        button.style.fontFamily = 'system-ui, -apple-system, sans-serif';
    }

    /**
     * Get link icon SVG
     */
    _getLinkIcon() {
        // CKEditor's default link icon
        return '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="m11.077 15 .991-1.416a.75.75 0 1 1 1.229.86l-1.148 1.64a.748.748 0 0 1-.217.206 5.251 5.251 0 0 1-8.503-5.955.741.741 0 0 1 .12-.274l1.147-1.639a.75.75 0 1 1 1.228.86L4.933 10.7l.006.003a3.75 3.75 0 0 0 6.132 4.294l.006.004zm5.494-5.335a.748.748 0 0 1-.12.274l-1.147 1.639a.75.75 0 1 1-1.228-.86l.86-1.23a3.75 3.75 0 0 0-6.144-4.301l-.86 1.229a.75.75 0 0 1-1.229-.86l1.148-1.64a.748.748 0 0 1 .217-.206 5.251 5.251 0 0 1 8.503 5.955z"/></svg>';
    }

    /**
     * Handle clicks to prevent balloon from closing when clicking merge tag modal
     */
    _handleDocumentMouseDown(event) {
        const targetElement = event.target;
        if (!(targetElement instanceof Element)) {
            return;
        }

        const editorRoot = this.editor?.editing?.view?.getDomRoot?.();
        const clickedAnchor = targetElement.closest('a');

        if (editorRoot && clickedAnchor && editorRoot.contains(clickedAnchor)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            this._currentLinkText = clickedAnchor.textContent || '';
            this._currentLinkUrl = clickedAnchor.getAttribute('href') || '';
            this._currentLinkElement = null;
            this._currentLinkRange = null;
            this._hasDomFallbackValues = true;

            setTimeout(() => {
                this._showBalloon(false);
                this._openEditorForSelectedLink();
            }, 0);

            return;
        }

        if (!this._balloon || !this._balloon.visibleView) {
            return;
        }

        const insideMergeTagModal = targetElement.closest('.modal-root, .MuiBackdrop-root');
        if (insideMergeTagModal) {
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
    }

    _handleDocumentMouseUp(event) {
        const targetElement = event.target;
        if (!(targetElement instanceof Element)) {
            return;
        }

        const editorRoot = this.editor?.editing?.view?.getDomRoot?.();
        if (!editorRoot || !editorRoot.contains(targetElement)) {
            return;
        }

        setTimeout(() => {
            if (this._openEditorForSelectedLink()) {
                return;
            }

            const selection = this.editor.model.document.selection;
            const linkHref = selection.getAttribute('linkHref');
            if (!linkHref) {
                return;
            }

            this._currentLinkElement = null;
            this._currentLinkRange = null;
            this._currentLinkUrl = linkHref;
            this._currentLinkText = this._getSelectedText() || '';
            this._hasDomFallbackValues = true;
            this._showBalloon(false);
        }, 0);
    }

    /**
     * Clean up when plugin is destroyed
     */
    destroy() {
        if (this._boundMouseDownHandler) {
            document.removeEventListener('mousedown', this._boundMouseDownHandler, true);
        }

        if (this._boundMouseUpHandler) {
            document.removeEventListener('mouseup', this._boundMouseUpHandler, true);
        }

        if (this._domRootWithListener && this._boundDomClickHandler) {
            this._domRootWithListener.removeEventListener('click', this._boundDomClickHandler, true);
            this._domRootWithListener = null;
        }

        if (this._balloon && this._formView) {
            this._balloon.remove(this._formView);
        }

        this._unmountPillEditors();

        super.destroy();
    }
}
